import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { miniPellesAPI, optionsAPI, categoriesAPI, uploadAPI, materialTypesAPI, brandsAPI } from '@/lib/api';
import { formatPrice, getImageUrl } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import CatalogueImportDialog from '@/components/CatalogueImportDialog';
import MediaSelector from '@/components/MediaSelector';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Package,
  Wrench,
  Euro,
  Upload,
  Image,
  Download,
  FileText,
  Search,
  CheckCircle,
  XCircle,
  LayoutGrid,
  List,
  SlidersHorizontal,
  Filter,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  TableIcon,
  FolderOpen,
  X,
  Eye,
  EyeOff,
  Images,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CataloguePage = () => {
  const { isAdmin, user } = useAuth();
  const [miniPelles, setMiniPelles] = useState([]);
  const [options, setOptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  
  // Permissions: vendeurs cannot delete products
  const canDelete = isAdmin() || user?.role === 'manager';
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'grid', 'list' (grouped), or 'table'
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  
  // Collapsed brands state (all collapsed by default)
  const [collapsedBrands, setCollapsedBrands] = useState({});
  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    image: true,
    name: true,
    brand: true,
    model: true,
    weight: true,
    price: true,
    warranty: true,
    actions: true,
  });
  
  // File input refs
  const mpFileInputRef = useRef(null);
  const optFileInputRef = useRef(null);
  const mpBrochureInputRef = useRef(null);
  const importFileInputRef = useRef(null);
  
  // Mini Pelle Dialog
  const [mpDialogOpen, setMpDialogOpen] = useState(false);
  const [editingMp, setEditingMp] = useState(null);
  const [mpForm, setMpForm] = useState({
    name: '',
    model: '',
    description: '',
    base_price: '',
    weight: '',
    power: '',
    image_url: '',
    brochure_url: '',
    brochure_downloadable: true,
    gallery_images: [],
    // Nouveaux champs
    brand: '',
    warranty: '',
    material_type_id: '',
    longueur: '',
    largeur: '',
    profondeur_fouille: '',
    hauteur_chargement: '',
    marque_moteur: '',
    nombre_cylindres: '',
    puissance_moteur: '',
    marque_pompe: '',
    debit_pompe: '',
    custom_fields: {},
    // Portail web
    visible_on_website: false,
    website_description: '',
    // Variantes
    has_variants: false,
    variants: [],
  });
  const [mpImagePreview, setMpImagePreview] = useState(null);
  const [uploadingMpImage, setUploadingMpImage] = useState(false);
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  
  // Media selector dialog
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [mediaDialogTarget, setMediaDialogTarget] = useState(null); // 'image', 'brochure', 'gallery', 'variant'
  const [variantMediaIndex, setVariantMediaIndex] = useState(null);
  
  // Media selector dialog for options
  const [optMediaDialogOpen, setOptMediaDialogOpen] = useState(false);
  
  // Option Dialog
  const [optDialogOpen, setOptDialogOpen] = useState(false);
  const [editingOpt, setEditingOpt] = useState(null);
  const [optForm, setOptForm] = useState({
    name: '',
    category: '',
    description: '',
    image_url: '',
    prices_by_model: {},
    compatibility_by_model: {}, // {mini_pelle_id: {compatible: bool, price: number|null, included: bool}}
  });
  const [optImagePreview, setOptImagePreview] = useState(null);
  const [uploadingOptImage, setUploadingOptImage] = useState(false);
  
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [mpRes, optRes, catRes, typesRes] = await Promise.all([
        miniPellesAPI.getAll(),
        optionsAPI.getAll(),
        categoriesAPI.getAll(),
        materialTypesAPI.getAll(),
      ]);
      setMiniPelles(mpRes.data);
      setOptions(optRes.data);
      setCategories(catRes.data);
      setMaterialTypes(typesRes.data);
      
      // Extract unique brands from mini pelles
      const uniqueBrands = [...new Set(mpRes.data.map(mp => mp.brand).filter(Boolean))].sort();
      setBrands(uniqueBrands);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter mini pelles
  const filteredMiniPelles = miniPelles.filter(mp => {
    const matchesSearch = mp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mp.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mp.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || mp.material_type_id === filterType;
    const matchesBrand = filterBrand === 'all' || mp.brand === filterBrand;
    return matchesSearch && matchesType && matchesBrand;
  });
  
  // Sorted mini pelles
  const sortedMiniPelles = useMemo(() => {
    const sorted = [...filteredMiniPelles];
    sorted.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle null/undefined
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      // Handle numeric fields
      if (sortConfig.key === 'base_price') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      // String comparison
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredMiniPelles, sortConfig]);
  
  // Group by brand for display (using sorted list)
  const groupedByBrand = sortedMiniPelles.reduce((acc, mp) => {
    const brand = mp.brand || 'Autres';
    if (!acc[brand]) acc[brand] = [];
    acc[brand].push(mp);
    return acc;
  }, {});
  
  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronDown className="w-4 h-4 text-slate-300" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-yellow-500" /> 
      : <ChevronDown className="w-4 h-4 text-yellow-500" />;
  };

  // Image upload handlers
  const handleMpImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingMpImage(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      setMpForm({ ...mpForm, image_url: response.data.url });
      setMpImagePreview(URL.createObjectURL(file));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingMpImage(false);
    }
  };

  const handleOptImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingOptImage(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      setOptForm({ ...optForm, image_url: response.data.url });
      setOptImagePreview(URL.createObjectURL(file));
      toast.success('Image uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploadingOptImage(false);
    }
  };

  // Mini Pelle handlers
  const handleMpSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    const data = {
      ...mpForm,
      base_price: parseFloat(mpForm.base_price) || 0,
    };
    
    try {
      if (editingMp) {
        await miniPellesAPI.update(editingMp.id, data);
        toast.success('Mini pelle mise à jour');
      } else {
        await miniPellesAPI.create(data);
        toast.success('Mini pelle créée');
      }
      setMpDialogOpen(false);
      resetMpForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditMp = (mp) => {
    setEditingMp(mp);
    setMpForm({
      name: mp.name,
      model: mp.model,
      description: mp.description || '',
      base_price: mp.base_price.toString(),
      weight: mp.weight || '',
      power: mp.power || '',
      image_url: mp.image_url || '',
      brochure_url: mp.brochure_url || '',
      brochure_downloadable: mp.brochure_downloadable !== false,
      gallery_images: mp.gallery_images || [],
      brand: mp.brand || '',
      warranty: mp.warranty || '',
      material_type_id: mp.material_type_id || '',
      longueur: mp.longueur || '',
      largeur: mp.largeur || '',
      profondeur_fouille: mp.profondeur_fouille || '',
      hauteur_chargement: mp.hauteur_chargement || '',
      marque_moteur: mp.marque_moteur || '',
      nombre_cylindres: mp.nombre_cylindres || '',
      puissance_moteur: mp.puissance_moteur || '',
      marque_pompe: mp.marque_pompe || '',
      debit_pompe: mp.debit_pompe || '',
      custom_fields: mp.custom_fields || {},
      visible_on_website: mp.visible_on_website || false,
      website_description: mp.website_description || '',
      has_variants: mp.has_variants || false,
      variants: mp.variants || [],
    });
    setMpImagePreview(mp.image_url || null);
    setMpDialogOpen(true);
  };

  const handleDeleteMp = async (mpId) => {
    if (!window.confirm('Supprimer cette mini pelle ?')) return;
    
    try {
      await miniPellesAPI.delete(mpId);
      toast.success('Mini pelle supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleBrochureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error('Veuillez sélectionner un fichier PDF');
      return;
    }
    
    setUploadingBrochure(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadAPI.uploadPDF(formData);
      setMpForm({ ...mpForm, brochure_url: response.data.url });
      toast.success('Brochure uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload de la brochure');
    } finally {
      setUploadingBrochure(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await miniPellesAPI.import(formData);
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await miniPellesAPI.export();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mini_pelles_export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const resetMpForm = () => {
    setEditingMp(null);
    setMpForm({
      name: '',
      model: '',
      description: '',
      base_price: '',
      weight: '',
      power: '',
      image_url: '',
      brochure_url: '',
      brochure_downloadable: true,
      gallery_images: [],
      brand: '',
      warranty: '',
      material_type_id: '',
      longueur: '',
      largeur: '',
      profondeur_fouille: '',
      hauteur_chargement: '',
      marque_moteur: '',
      nombre_cylindres: '',
      puissance_moteur: '',
      marque_pompe: '',
      debit_pompe: '',
      custom_fields: {},
      visible_on_website: false,
      website_description: '',
      has_variants: false,
      variants: [],
    });
    setMpImagePreview(null);
  };

  // Variant handlers
  const addVariant = () => {
    const newVariant = {
      id: `variant-${Date.now()}`,
      name: '',
      price: mpForm.base_price || 0,
      image_url: ''
    };
    setMpForm({
      ...mpForm,
      variants: [...mpForm.variants, newVariant]
    });
  };

  const updateVariant = (index, field, value) => {
    const updatedVariants = [...mpForm.variants];
    updatedVariants[index] = {
      ...updatedVariants[index],
      [field]: field === 'price' ? parseFloat(value) || 0 : value
    };
    setMpForm({ ...mpForm, variants: updatedVariants });
  };

  const removeVariant = (index) => {
    const updatedVariants = mpForm.variants.filter((_, i) => i !== index);
    setMpForm({ 
      ...mpForm, 
      variants: updatedVariants,
      has_variants: updatedVariants.length > 0 ? mpForm.has_variants : false
    });
  };

  // Option handlers
  const handleOptSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      if (editingOpt) {
        await optionsAPI.update(editingOpt.id, optForm);
        toast.success('Option mise à jour');
      } else {
        await optionsAPI.create(optForm);
        toast.success('Option créée');
      }
      setOptDialogOpen(false);
      resetOptForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditOpt = (opt) => {
    setEditingOpt(opt);
    // Migrate old prices_by_model to compatibility_by_model if needed
    let compatibility = opt.compatibility_by_model || {};
    if (Object.keys(compatibility).length === 0 && opt.prices_by_model) {
      // Convert legacy format
      Object.entries(opt.prices_by_model).forEach(([mpId, price]) => {
        compatibility[mpId] = { compatible: true, price: price, included: false };
      });
    }
    setOptForm({
      name: opt.name,
      category: opt.category,
      description: opt.description || '',
      image_url: opt.image_url || '',
      prices_by_model: opt.prices_by_model || {},
      compatibility_by_model: compatibility,
    });
    setOptImagePreview(opt.image_url || null);
    setOptDialogOpen(true);
  };

  const handleDeleteOpt = async (optId) => {
    if (!window.confirm('Supprimer cette option ?')) return;
    
    try {
      await optionsAPI.delete(optId);
      toast.success('Option supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetOptForm = () => {
    setEditingOpt(null);
    setOptForm({
      name: '',
      category: '',
      description: '',
      image_url: '',
      prices_by_model: {},
      compatibility_by_model: {},
    });
    setOptImagePreview(null);
  };

  const updateOptionCompatibility = (mpId, field, value) => {
    const current = optForm.compatibility_by_model[mpId] || { compatible: true, price: null, included: false };
    const updated = { ...current, [field]: value };
    
    // If marking as included, set price to null
    if (field === 'included' && value === true) {
      updated.price = null;
    }
    // If marking as not compatible, reset price and included
    if (field === 'compatible' && value === false) {
      updated.price = null;
      updated.included = false;
    }
    
    // Also update legacy prices_by_model for backward compatibility
    const newPricesByModel = { ...optForm.prices_by_model };
    if (updated.compatible && !updated.included && updated.price !== null) {
      newPricesByModel[mpId] = updated.price;
    } else {
      delete newPricesByModel[mpId];
    }
    
    setOptForm({
      ...optForm,
      compatibility_by_model: {
        ...optForm.compatibility_by_model,
        [mpId]: updated,
      },
      prices_by_model: newPricesByModel,
    });
  };

  const updateOptionPrice = (mpId, price) => {
    setOptForm({
      ...optForm,
      prices_by_model: {
        ...optForm.prices_by_model,
        [mpId]: parseFloat(price) || 0,
      },
    });
  };

  // Group mini pelles by brand (defined above at line 185)

  // Initialize collapsed state for all brands (collapsed by default)
  useEffect(() => {
    const brands = Object.keys(groupedByBrand);
    setCollapsedBrands(prev => {
      const newState = { ...prev };
      brands.forEach(brand => {
        if (!(brand in newState)) {
          newState[brand] = true; // Collapsed by default
        }
      });
      return newState;
    });
  }, [filteredMiniPelles.length]);

  const toggleBrandCollapse = (brand) => {
    setCollapsedBrands(prev => ({
      ...prev,
      [brand]: !prev[brand]
    }));
  };

  const expandAllBrands = () => {
    const allExpanded = {};
    Object.keys(groupedByBrand).forEach(brand => {
      allExpanded[brand] = false;
    });
    setCollapsedBrands(allExpanded);
  };

  const collapseAllBrands = () => {
    const allCollapsed = {};
    Object.keys(groupedByBrand).forEach(brand => {
      allCollapsed[brand] = true;
    });
    setCollapsedBrands(allCollapsed);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalogue-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
          Catalogue
        </h1>
        <p className="text-slate-500 mt-1">Gérez vos mini pelles et leurs options</p>
      </div>

      <Tabs defaultValue="mini-pelles">
        <TabsList className="mb-6">
          <TabsTrigger value="mini-pelles" data-testid="tab-mini-pelles">
            <Package className="w-4 h-4 mr-2" />
            Mini Pelles ({miniPelles.length})
          </TabsTrigger>
          <TabsTrigger value="options" data-testid="tab-options">
            <Wrench className="w-4 h-4 mr-2" />
            Options ({options.length})
          </TabsTrigger>
        </TabsList>

        {/* Mini Pelles Tab */}
        <TabsContent value="mini-pelles">
          <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
            {/* Import/Export buttons (admin only) */}
            {isAdmin() && (
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setImportDialogOpen(true)}
                  data-testid="import-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importer CSV
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleExport}
                  data-testid="export-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exporter CSV
                </Button>
              </div>
            )}
            <Dialog open={mpDialogOpen} onOpenChange={(open) => {
              setMpDialogOpen(open);
              if (!open) resetMpForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-slate-900" data-testid="new-mini-pelle-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle mini pelle
                </Button>
              </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                      {editingMp ? 'Modifier la mini pelle' : 'Nouvelle mini pelle'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleMpSubmit} className="space-y-4">
                    {/* Image Upload */}
                    <div className="space-y-2">
                      <Label>Image principale de l'engin</Label>
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-32 h-24 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-yellow-500 transition-colors"
                          onClick={() => mpFileInputRef.current?.click()}
                        >
                          {mpImagePreview || getImageUrl(mpForm.image_url) ? (
                            <img 
                              src={mpImagePreview || getImageUrl(mpForm.image_url)} 
                              alt="Preview" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-center text-slate-400">
                              <Image className="w-8 h-8 mx-auto mb-1" />
                              <span className="text-xs">Cliquer</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 space-y-2">
                          <input
                            ref={mpFileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleMpImageUpload}
                            className="hidden"
                          />
                          <div className="flex gap-2">
                            <Button 
                              type="button" 
                              variant="outline" 
                              onClick={() => mpFileInputRef.current?.click()}
                              disabled={uploadingMpImage}
                            >
                              {uploadingMpImage ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Upload className="w-4 h-4 mr-2" />
                              )}
                              Importer
                            </Button>
                            <Button 
                              type="button" 
                              variant="outline"
                              onClick={() => { setMediaDialogTarget('image'); setMediaDialogOpen(true); }}
                              className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                            >
                              <FolderOpen className="w-4 h-4 mr-2" />
                              GED
                            </Button>
                          </div>
                          <p className="text-xs text-slate-500">JPG, PNG ou WebP. Max 5MB</p>
                          <div className="pt-2 border-t">
                            <Label htmlFor="mp-image-url" className="text-xs text-slate-600">Ou URL externe (Dropbox, etc.)</Label>
                            <Input
                              id="mp-image-url"
                              value={mpForm.image_url}
                              onChange={(e) => {
                                setMpForm({ ...mpForm, image_url: e.target.value });
                                setMpImagePreview(null);
                              }}
                              placeholder="https://..."
                              className="mt-1 text-sm"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="mp-name">Nom *</Label>
                        <Input
                          id="mp-name"
                          value={mpForm.name}
                          onChange={(e) => setMpForm({ ...mpForm, name: e.target.value })}
                          required
                          data-testid="mp-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mp-model">Modèle *</Label>
                        <Input
                          id="mp-model"
                          value={mpForm.model}
                          onChange={(e) => setMpForm({ ...mpForm, model: e.target.value })}
                          required
                          data-testid="mp-model-input"
                        />
                      </div>
                    </div>
                    
                    {/* Nouveaux champs: Marque, Type, Garantie */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="mp-brand">Marque</Label>
                        <Input
                          id="mp-brand"
                          value={mpForm.brand}
                          onChange={(e) => setMpForm({ ...mpForm, brand: e.target.value })}
                          placeholder="Ex: Kubota, Liugong..."
                          data-testid="mp-brand-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Type de matériel</Label>
                        <Select 
                          value={mpForm.material_type_id || 'none'} 
                          onValueChange={(v) => setMpForm({ ...mpForm, material_type_id: v === 'none' ? '' : v })}
                        >
                          <SelectTrigger data-testid="mp-type-select">
                            <SelectValue placeholder="Sélectionner..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Aucun</SelectItem>
                            {materialTypes.map((type) => (
                              <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mp-warranty">Garantie</Label>
                        <Input
                          id="mp-warranty"
                          value={mpForm.warranty}
                          onChange={(e) => setMpForm({ ...mpForm, warranty: e.target.value })}
                          placeholder="Ex: 2 ans pièces et MO"
                          data-testid="mp-warranty-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="mp-description">Description</Label>
                      <Textarea
                        id="mp-description"
                        value={mpForm.description}
                        onChange={(e) => setMpForm({ ...mpForm, description: e.target.value })}
                        rows={2}
                        data-testid="mp-description-input"
                        placeholder="Description de l'engin qui sera affichée sur le devis..."
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="mp-price">Prix de base (€) *</Label>
                        <Input
                          id="mp-price"
                          type="number"
                          step="0.01"
                          value={mpForm.base_price}
                          onChange={(e) => setMpForm({ ...mpForm, base_price: e.target.value })}
                          required
                          data-testid="mp-price-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mp-weight">Poids</Label>
                        <Input
                          id="mp-weight"
                          value={mpForm.weight}
                          onChange={(e) => setMpForm({ ...mpForm, weight: e.target.value })}
                          placeholder="ex: 1.5T"
                          data-testid="mp-weight-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mp-power">Puissance générale</Label>
                        <Input
                          id="mp-power"
                          value={mpForm.power}
                          onChange={(e) => setMpForm({ ...mpForm, power: e.target.value })}
                          placeholder="ex: 15kW"
                          data-testid="mp-power-input"
                        />
                      </div>
                    </div>

                    {/* Caractéristiques techniques */}
                    <div className="border-t border-slate-200 pt-4 mt-4">
                      <h4 className="font-semibold text-slate-800 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                        CARACTÉRISTIQUES TECHNIQUES
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="mp-longueur" className="text-xs">Longueur</Label>
                          <Input
                            id="mp-longueur"
                            value={mpForm.longueur}
                            onChange={(e) => setMpForm({ ...mpForm, longueur: e.target.value })}
                            placeholder="ex: 3.5m"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="mp-largeur" className="text-xs">Largeur</Label>
                          <Input
                            id="mp-largeur"
                            value={mpForm.largeur}
                            onChange={(e) => setMpForm({ ...mpForm, largeur: e.target.value })}
                            placeholder="ex: 1.2m"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="mp-profondeur" className="text-xs">Profondeur de fouille</Label>
                          <Input
                            id="mp-profondeur"
                            value={mpForm.profondeur_fouille}
                            onChange={(e) => setMpForm({ ...mpForm, profondeur_fouille: e.target.value })}
                            placeholder="ex: 2.5m"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="mp-hauteur" className="text-xs">Hauteur de chargement</Label>
                          <Input
                            id="mp-hauteur"
                            value={mpForm.hauteur_chargement}
                            onChange={(e) => setMpForm({ ...mpForm, hauteur_chargement: e.target.value })}
                            placeholder="ex: 2.8m"
                            className="h-9"
                          />
                        </div>
                      </div>

                      <h4 className="font-semibold text-slate-800 mt-4 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                        MOTEUR
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="mp-marque-moteur" className="text-xs">Marque du moteur</Label>
                          <Input
                            id="mp-marque-moteur"
                            value={mpForm.marque_moteur}
                            onChange={(e) => setMpForm({ ...mpForm, marque_moteur: e.target.value })}
                            placeholder="ex: Kubota"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="mp-cylindres" className="text-xs">Nombre de cylindres</Label>
                          <Input
                            id="mp-cylindres"
                            value={mpForm.nombre_cylindres}
                            onChange={(e) => setMpForm({ ...mpForm, nombre_cylindres: e.target.value })}
                            placeholder="ex: 3"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="mp-puissance-moteur" className="text-xs">Puissance moteur</Label>
                          <Input
                            id="mp-puissance-moteur"
                            value={mpForm.puissance_moteur}
                            onChange={(e) => setMpForm({ ...mpForm, puissance_moteur: e.target.value })}
                            placeholder="ex: 15kW / 20CV"
                            className="h-9"
                          />
                        </div>
                      </div>

                      <h4 className="font-semibold text-slate-800 mt-4 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>
                        HYDRAULIQUE
                      </h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="mp-marque-pompe" className="text-xs">Marque de la pompe</Label>
                          <Input
                            id="mp-marque-pompe"
                            value={mpForm.marque_pompe}
                            onChange={(e) => setMpForm({ ...mpForm, marque_pompe: e.target.value })}
                            placeholder="ex: Rexroth"
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="mp-debit-pompe" className="text-xs">Débit pompe (L/min)</Label>
                          <Input
                            id="mp-debit-pompe"
                            value={mpForm.debit_pompe}
                            onChange={(e) => setMpForm({ ...mpForm, debit_pompe: e.target.value })}
                            placeholder="ex: 45 L/min"
                            className="h-9"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Portail Web Section */}
                    <div className="space-y-4 border-t pt-4">
                      <Label className="flex items-center gap-2 text-yellow-600 font-medium">
                        <Package className="w-4 h-4" />
                        Portail Web Public
                      </Label>
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="visible_on_website"
                          checked={mpForm.visible_on_website}
                          onChange={(e) => setMpForm({ ...mpForm, visible_on_website: e.target.checked })}
                          className="w-5 h-5 rounded border-slate-300 text-yellow-500 focus:ring-yellow-500"
                          data-testid="visible-on-website-checkbox"
                        />
                        <Label htmlFor="visible_on_website" className="cursor-pointer">
                          Afficher sur le site web public
                        </Label>
                      </div>
                      {mpForm.visible_on_website && (
                        <div className="space-y-2 pl-8">
                          <Label htmlFor="website_description">Description pour le site web</Label>
                          <Textarea
                            id="website_description"
                            value={mpForm.website_description}
                            onChange={(e) => setMpForm({ ...mpForm, website_description: e.target.value })}
                            placeholder="Description attractive pour le site web (optionnel)"
                            rows={3}
                            data-testid="website-description-textarea"
                          />
                          <p className="text-xs text-slate-500">Si vide, la description standard sera utilisée</p>
                        </div>
                      )}
                    </div>

                    {/* Variantes */}
                    <div className="space-y-3 border-t pt-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="has_variants"
                          checked={mpForm.has_variants}
                          onChange={(e) => setMpForm({ ...mpForm, has_variants: e.target.checked })}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <Label htmlFor="has_variants" className="cursor-pointer font-medium">
                          Ce modèle a des variantes (versions différentes)
                        </Label>
                      </div>
                      
                      {mpForm.has_variants && (
                        <div className="space-y-3 pl-4 border-l-2 border-yellow-400">
                          <p className="text-sm text-slate-600">
                            Ajoutez les différentes versions de ce modèle (ex: Standard, Canopy, Cabine...)
                          </p>
                          
                          {mpForm.variants.map((variant, index) => (
                            <div key={variant.id || index} className="bg-slate-50 p-3 rounded-lg space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-slate-700">Variante {index + 1}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-red-500 hover:text-red-600"
                                  onClick={() => removeVariant(index)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Nom de la variante *</Label>
                                  <Input
                                    value={variant.name}
                                    onChange={(e) => updateVariant(index, 'name', e.target.value)}
                                    placeholder="Ex: Version Canopy"
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Prix HT (€) *</Label>
                                  <Input
                                    type="number"
                                    value={variant.price}
                                    onChange={(e) => updateVariant(index, 'price', e.target.value)}
                                    placeholder="0"
                                    className="h-9"
                                  />
                                </div>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Image (optionnel)</Label>
                                <div className="flex gap-2 items-center">
                                  {variant.image_url && (
                                    <img
                                      src={variant.image_url}
                                      alt={variant.name}
                                      className="w-10 h-8 object-cover rounded border border-slate-200 shrink-0"
                                    />
                                  )}
                                  <Input
                                    value={variant.image_url || ''}
                                    onChange={(e) => updateVariant(index, 'image_url', e.target.value)}
                                    placeholder="https://... ou choisir depuis la GED"
                                    className="h-9 text-sm flex-1"
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-9 shrink-0 text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                                    title="Choisir depuis la GED"
                                    onClick={() => {
                                      setVariantMediaIndex(index);
                                      setMediaDialogTarget('variant');
                                      setMediaDialogOpen(true);
                                    }}
                                  >
                                    <FolderOpen className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                          
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addVariant}
                            className="w-full border-dashed"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter une variante
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Brochure Upload */}
                    <div className="space-y-3 border-t pt-4">
                      <Label className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Brochure commerciale (PDF)
                      </Label>
                      <div className="flex items-center gap-3 flex-wrap">
                        <input
                          ref={mpBrochureInputRef}
                          type="file"
                          accept=".pdf"
                          onChange={handleBrochureUpload}
                          className="hidden"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => mpBrochureInputRef.current?.click()}
                          disabled={uploadingBrochure}
                        >
                          {uploadingBrochure ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="w-4 h-4 mr-2" />
                          )}
                          {mpForm.brochure_url ? 'Remplacer' : 'Importer'}
                        </Button>
                        <Button 
                          type="button" 
                          variant="outline"
                          size="sm"
                          onClick={() => { setMediaDialogTarget('brochure'); setMediaDialogOpen(true); }}
                          className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                        >
                          <FolderOpen className="w-4 h-4 mr-2" />
                          GED
                        </Button>
                        {mpForm.brochure_url && (
                          <div className="flex items-center gap-2 text-sm text-green-600">
                            <FileText className="w-4 h-4" />
                            <span>Brochure ajoutée</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-500 h-6 px-2"
                              onClick={() => setMpForm({ ...mpForm, brochure_url: '' })}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      {mpForm.brochure_url && (
                        <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                          <input
                            type="checkbox"
                            id="brochure-downloadable"
                            checked={mpForm.brochure_downloadable !== false}
                            onChange={(e) => setMpForm({ ...mpForm, brochure_downloadable: e.target.checked })}
                            className="w-4 h-4 rounded border-slate-300"
                          />
                          <label htmlFor="brochure-downloadable" className="text-sm text-slate-700 flex items-center gap-2">
                            {mpForm.brochure_downloadable !== false ? (
                              <>
                                <Download className="w-4 h-4 text-green-600" />
                                Téléchargement autorisé
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 text-blue-600" />
                                E-Catalogue uniquement (consultation sans téléchargement)
                              </>
                            )}
                          </label>
                        </div>
                      )}
                      <p className="text-xs text-slate-500">Cette brochure pourra être jointe aux emails de devis et affichée sur le portail</p>
                    </div>

                    {/* Galerie d'images */}
                    <div className="space-y-3 border-t pt-4">
                      <Label className="flex items-center gap-2">
                        <Images className="w-4 h-4" />
                        Images supplémentaires (galerie portail)
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {(mpForm.gallery_images || []).map((img, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={getImageUrl(img)} 
                              alt={`Galerie ${idx + 1}`}
                              className="w-16 h-16 object-cover rounded-lg border"
                            />
                            <button
                              type="button"
                              onClick={() => setMpForm({ 
                                ...mpForm, 
                                gallery_images: mpForm.gallery_images.filter((_, i) => i !== idx) 
                              })}
                              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          className="w-16 h-16 flex flex-col items-center justify-center text-slate-400 hover:text-yellow-600 hover:border-yellow-300"
                          onClick={() => { setMediaDialogTarget('gallery'); setMediaDialogOpen(true); }}
                        >
                          <Plus className="w-5 h-5" />
                          <span className="text-[10px]">GED</span>
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">Ces images apparaîtront dans la galerie du produit sur le portail public</p>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                      <Button type="button" variant="outline" onClick={() => setMpDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button
                        type="submit"
                        className="bg-slate-800 hover:bg-slate-900"
                        disabled={formLoading}
                        data-testid="save-mp-btn"
                      >
                        {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {editingMp ? 'Mettre à jour' : 'Créer'}
                      </Button>
                    </div>
                  </form>

                  {/* Media Selector Dialog */}
                  <MediaSelector
                    open={mediaDialogOpen}
                    onOpenChange={setMediaDialogOpen}
                    fileType={mediaDialogTarget === 'brochure' ? 'pdf' : 'image'}
                    multiple={mediaDialogTarget === 'gallery'}
                    title={
                      mediaDialogTarget === 'image' ? 'Sélectionner une image principale' :
                      mediaDialogTarget === 'brochure' ? 'Sélectionner une brochure' :
                      'Sélectionner des images pour la galerie'
                    }
                    onSelect={(selected) => {
                      if (mediaDialogTarget === 'image') {
                        setMpForm({ ...mpForm, image_url: selected.url });
                        setMpImagePreview(null);
                      } else if (mediaDialogTarget === 'variant') {
                        if (variantMediaIndex !== null) {
                          updateVariant(variantMediaIndex, 'image_url', selected.url);
                        }
                      } else if (mediaDialogTarget === 'brochure') {
                        setMpForm({ ...mpForm, brochure_url: selected.url });
                      } else if (mediaDialogTarget === 'gallery') {
                        const newImages = Array.isArray(selected) 
                          ? selected.map(s => s.url) 
                          : [selected.url];
                        setMpForm({ 
                          ...mpForm, 
                          gallery_images: [...(mpForm.gallery_images || []), ...newImages] 
                        });
                      }
                    }}
                  />
                </DialogContent>
              </Dialog>
          </div>

          {/* Search bar, filters and view toggle for mini pelles */}
          <div className="mb-4 space-y-3">
            {/* Filters Row */}
            <div className="flex flex-wrap gap-3 items-center">
              {/* Type filter */}
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Type de matériel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {materialTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Brand filter */}
              <Select value={filterBrand} onValueChange={setFilterBrand}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Marque" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les marques</SelectItem>
                  {brands.map((brand) => (
                    <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Column visibility */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <SlidersHorizontal className="w-4 h-4 mr-2" />
                    Colonnes
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.image}
                    onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, image: checked})}
                  >
                    Image
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.name}
                    onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, name: checked})}
                  >
                    Nom
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.brand}
                    onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, brand: checked})}
                  >
                    Marque
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.model}
                    onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, model: checked})}
                  >
                    Modèle
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.weight}
                    onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, weight: checked})}
                  >
                    Poids
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.warranty}
                    onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, warranty: checked})}
                  >
                    Garantie
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuCheckboxItem
                    checked={visibleColumns.price}
                    onCheckedChange={(checked) => setVisibleColumns({...visibleColumns, price: checked})}
                  >
                    Prix
                  </DropdownMenuCheckboxItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Search and view toggle */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher un engin..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-machines-input"
                />
              </div>
              <div className="flex items-center gap-1 border rounded-lg p-1 bg-slate-50">
                <Button
                  variant={viewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('table')}
                  className={viewMode === 'table' ? 'bg-slate-800' : ''}
                  data-testid="view-table-btn"
                  title="Vue tableau"
                >
                  <TableIcon className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={viewMode === 'grid' ? 'bg-slate-800' : ''}
                  data-testid="view-grid-btn"
                  title="Vue grille"
                >
                  <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' ? 'bg-slate-800' : ''}
                  data-testid="view-list-btn"
                  title="Vue par marque"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Table View - Like other lists */}
          {viewMode === 'table' && (
          <div className="border rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  {visibleColumns.image && (
                    <TableHead className="w-20 text-xs font-bold uppercase tracking-wider text-slate-500">
                      Image
                    </TableHead>
                  )}
                  <TableHead 
                    className="text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-700"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Nom {getSortIcon('name')}
                    </div>
                  </TableHead>
                  {visibleColumns.brand && (
                    <TableHead 
                      className="text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-700 hidden md:table-cell"
                      onClick={() => handleSort('brand')}
                    >
                      <div className="flex items-center gap-1">
                        Marque {getSortIcon('brand')}
                      </div>
                    </TableHead>
                  )}
                  {visibleColumns.model && (
                    <TableHead 
                      className="text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-700 hidden sm:table-cell"
                      onClick={() => handleSort('model')}
                    >
                      <div className="flex items-center gap-1">
                        Modèle {getSortIcon('model')}
                      </div>
                    </TableHead>
                  )}
                  {visibleColumns.weight && (
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">
                      Poids
                    </TableHead>
                  )}
                  {visibleColumns.price && (
                    <TableHead 
                      className="text-xs font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-700 text-right"
                      onClick={() => handleSort('base_price')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Prix {getSortIcon('base_price')}
                      </div>
                    </TableHead>
                  )}
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMiniPelles.map((mp) => (
                  <TableRow 
                    key={mp.id} 
                    className="hover:bg-slate-50 cursor-pointer transition-colors" 
                    data-testid={`mp-table-row-${mp.id}`}
                    onClick={() => handleEditMp(mp)}
                  >
                    {visibleColumns.image && (
                      <TableCell className="py-2">
                        <div className="w-16 h-12 bg-slate-100 rounded overflow-hidden">
                          {getImageUrl(mp.image_url) ? (
                            <img
                              src={getImageUrl(mp.image_url)}
                              alt={mp.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-6 h-6 text-slate-300" />
                            </div>
                          )}
                        </div>
                      </TableCell>
                    )}
                    <TableCell>
                      <div>
                        <p className="font-semibold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                          {mp.name}
                        </p>
                        {mp.visible_on_website && (
                          <Badge className="bg-yellow-100 text-yellow-700 text-[10px] mt-1">Portail</Badge>
                        )}
                      </div>
                    </TableCell>
                    {visibleColumns.brand && (
                      <TableCell className="hidden md:table-cell">
                        {mp.brand && <Badge variant="outline" className="text-xs">{mp.brand}</Badge>}
                      </TableCell>
                    )}
                    {visibleColumns.model && (
                      <TableCell className="text-sm text-slate-600 hidden sm:table-cell">
                        {mp.model}
                      </TableCell>
                    )}
                    {visibleColumns.weight && (
                      <TableCell className="text-sm text-slate-500 hidden lg:table-cell">
                        {mp.weight}
                      </TableCell>
                    )}
                    {visibleColumns.price && (
                      <TableCell className="text-right">
                        <span className="font-mono font-bold text-yellow-500">
                          {formatPrice(mp.base_price)}
                        </span>
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleEditMp(mp)}
                          data-testid={`edit-mp-table-${mp.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {canDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                          onClick={() => handleDeleteMp(mp.id)}
                          data-testid={`delete-mp-table-${mp.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {sortedMiniPelles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      {searchTerm || filterType !== 'all' || filterBrand !== 'all' 
                        ? 'Aucun engin trouvé avec ces critères' 
                        : 'Aucune mini pelle dans le catalogue'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredMiniPelles.map((mp) => (
              <Card key={mp.id} className="border-slate-200 overflow-hidden" data-testid={`mp-card-${mp.id}`}>
                <div className="aspect-[4/3] bg-slate-100 relative">
                  {getImageUrl(mp.image_url) ? (
                    <img
                      src={getImageUrl(mp.image_url)}
                      alt={mp.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-10 h-10 text-slate-300" />
                    </div>
                  )}
                </div>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-slate-800 text-sm truncate" style={{ fontFamily: 'Barlow Condensed' }}>
                        {mp.name}
                      </h3>
                      <p className="text-xs text-slate-500 truncate">{mp.model}</p>
                    </div>
                    <p className="font-mono text-sm font-bold text-yellow-500 ml-2">
                      {formatPrice(mp.base_price)}
                    </p>
                  </div>
                  {mp.description && (
                    <p className="text-xs text-slate-600 mt-1 line-clamp-1">{mp.description}</p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-2">
                    {mp.brand && <Badge variant="outline" className="text-[10px]">{mp.brand}</Badge>}
                    {mp.visible_on_website && (
                      <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">Portail</Badge>
                    )}
                    {mp.weight && <span>{mp.weight}</span>}
                    {mp.power && <span>• {mp.power}</span>}
                  </div>
                  <div className="flex items-center gap-1 mt-3 pt-2 border-t border-slate-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => handleEditMp(mp)}
                      data-testid={`edit-mp-${mp.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-red-500 hover:text-red-600"
                      onClick={() => handleDeleteMp(mp.id)}
                      data-testid={`delete-mp-${mp.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredMiniPelles.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500">
                {searchTerm || filterType !== 'all' || filterBrand !== 'all' 
                  ? 'Aucun engin trouvé avec ces critères' 
                  : 'Aucune mini pelle dans le catalogue'}
              </div>
            )}
          </div>
          )}

          {/* List View - Grouped by Brand */}
          {viewMode === 'list' && (
          <div className="space-y-4">
            {/* Expand/Collapse All buttons */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={expandAllBrands}>
                Tout déplier
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAllBrands}>
                Tout replier
              </Button>
            </div>

            {Object.entries(groupedByBrand).sort((a, b) => a[0].localeCompare(b[0])).map(([brand, machines]) => (
              <div key={brand} className="border rounded-lg overflow-hidden bg-white">
                {/* Brand Header - Clickable to toggle */}
                <button
                  onClick={() => toggleBrandCollapse(brand)}
                  className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {collapsedBrands[brand] ? (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-500" />
                    )}
                    <h3 className="font-bold text-lg text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
                      {brand}
                    </h3>
                    <Badge variant="secondary" className="text-xs">
                      {machines.length} engin{machines.length > 1 ? 's' : ''}
                    </Badge>
                  </div>
                </button>

                {/* Machines List - Collapsible */}
                {!collapsedBrands[brand] && (
                  <div className="divide-y divide-slate-100">
                    {machines.map((mp) => (
                      <div key={mp.id} className="p-4" data-testid={`mp-list-${mp.id}`}>
                        <div className="flex items-center gap-4">
                          {/* Image */}
                          {visibleColumns.image && (
                          <div className="w-24 h-20 sm:w-32 sm:h-24 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                            {getImageUrl(mp.image_url) ? (
                              <img
                                src={getImageUrl(mp.image_url)}
                                alt={mp.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-8 h-8 text-slate-300" />
                              </div>
                            )}
                          </div>
                          )}
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                {visibleColumns.name && (
                                <h3 className="font-semibold text-slate-800 text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                                  {mp.name}
                                </h3>
                                )}
                                <div className="flex items-center gap-2 flex-wrap">
                                  {visibleColumns.model && mp.model && (
                                    <span className="text-sm text-slate-500">{mp.model}</span>
                                  )}
                                  {mp.visible_on_website && (
                                    <Badge className="bg-yellow-100 text-yellow-700 text-xs">Portail</Badge>
                                  )}
                                </div>
                              </div>
                              {visibleColumns.price && (
                              <p className="font-mono text-lg font-bold text-yellow-500 flex-shrink-0">
                                {formatPrice(mp.base_price)}
                              </p>
                              )}
                            </div>
                            
                            {mp.description && (
                              <p className="text-sm text-slate-600 mt-1 line-clamp-2 hidden sm:block">{mp.description}</p>
                            )}
                            
                            {/* Specs row */}
                            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-500">
                              {visibleColumns.weight && mp.weight && (
                                <span className="bg-slate-100 px-2 py-1 rounded">Poids: {mp.weight}</span>
                              )}
                              {mp.power && (
                                <span className="bg-slate-100 px-2 py-1 rounded">Puissance: {mp.power}</span>
                              )}
                              {visibleColumns.warranty && mp.warranty && (
                                <span className="bg-green-100 text-green-700 px-2 py-1 rounded">Garantie: {mp.warranty}</span>
                              )}
                              {mp.longueur && (
                                <span className="bg-slate-100 px-2 py-1 rounded hidden md:inline">L: {mp.longueur}</span>
                              )}
                              {mp.largeur && (
                                <span className="bg-slate-100 px-2 py-1 rounded hidden md:inline">l: {mp.largeur}</span>
                              )}
                              {mp.profondeur_fouille && (
                                <span className="bg-slate-100 px-2 py-1 rounded hidden lg:inline">Prof: {mp.profondeur_fouille}</span>
                              )}
                              {mp.brochure_url && (
                                <a 
                                  href={getImageUrl(mp.brochure_url) || mp.brochure_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="bg-blue-100 text-blue-700 px-2 py-1 rounded flex items-center gap-1 hover:bg-blue-200"
                                >
                                  <FileText className="w-3 h-3" />
                                  Brochure
                                </a>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          {visibleColumns.actions !== false && (
                          <div className="flex flex-col sm:flex-row items-center gap-1 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditMp(mp)}
                              data-testid={`edit-mp-list-${mp.id}`}
                            >
                              <Pencil className="w-4 h-4 sm:mr-1" />
                              <span className="hidden sm:inline">Modifier</span>
                            </Button>
                            {canDelete && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteMp(mp.id)}
                              className="text-red-500 hover:text-red-600 border-red-200 hover:border-red-300"
                              data-testid={`delete-mp-list-${mp.id}`}
                            >
                              <Trash2 className="w-4 h-4 sm:mr-1" />
                              <span className="hidden sm:inline">Supprimer</span>
                            </Button>
                            )}
                          </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {filteredMiniPelles.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                {searchTerm || filterType !== 'all' || filterBrand !== 'all' 
                  ? 'Aucun engin trouvé avec ces critères' 
                  : 'Aucune mini pelle dans le catalogue'}
              </div>
            )}
          </div>
          )}
        </TabsContent>

        {/* Options Tab */}
        <TabsContent value="options">
          <div className="flex justify-end mb-4">
            <Dialog open={optDialogOpen} onOpenChange={(open) => {
              setOptDialogOpen(open);
              if (!open) resetOptForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-slate-900" data-testid="new-option-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvelle option
                </Button>
              </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
                      {editingOpt ? 'Modifier l\'option' : 'Nouvelle option'}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleOptSubmit} className="space-y-4">
                    {/* Image Upload */}
                    <div className="space-y-2">
                      <Label>Image de l'option</Label>
                      <div className="flex items-start gap-4">
                        <div 
                          className="w-24 h-24 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden cursor-pointer hover:border-yellow-500 transition-colors"
                          onClick={() => optFileInputRef.current?.click()}
                        >
                          {optImagePreview || getImageUrl(optForm.image_url) ? (
                            <img 
                              src={optImagePreview || getImageUrl(optForm.image_url)} 
                              alt="Preview" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="text-center text-slate-400">
                              <Image className="w-6 h-6 mx-auto mb-1" />
                              <span className="text-xs">Cliquer</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <input
                            ref={optFileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleOptImageUpload}
                            className="hidden"
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => optFileInputRef.current?.click()}
                            disabled={uploadingOptImage}
                          >
                            {uploadingOptImage ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            Importer
                          </Button>
                          <Button 
                            type="button" 
                            variant="outline"
                            onClick={() => setOptMediaDialogOpen(true)}
                            className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                          >
                            <FolderOpen className="w-4 h-4 mr-2" />
                            GED
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="opt-name">Nom *</Label>
                      <Input
                        id="opt-name"
                        value={optForm.name}
                        onChange={(e) => setOptForm({ ...optForm, name: e.target.value })}
                        required
                        data-testid="opt-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="opt-category">Catégorie *</Label>
                      <Select
                        value={optForm.category}
                        onValueChange={(value) => setOptForm({ ...optForm, category: value })}
                      >
                        <SelectTrigger data-testid="opt-category-select">
                          <SelectValue placeholder="Sélectionner une catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="opt-description">Description</Label>
                      <Textarea
                        id="opt-description"
                        value={optForm.description}
                        onChange={(e) => setOptForm({ ...optForm, description: e.target.value })}
                        rows={2}
                        data-testid="opt-description-input"
                      />
                    </div>
                    
                    {/* Compatibility by model */}
                    {miniPelles.length > 0 && (
                      <div className="space-y-3">
                        <Label className="flex items-center gap-2">
                          <Package className="w-4 h-4" />
                          Compatibilité par machine
                        </Label>
                        <div className="space-y-2 max-h-64 overflow-y-auto p-2 bg-slate-50 rounded-md">
                          {miniPelles.map((mp) => {
                            const compat = optForm.compatibility_by_model[mp.id] || { compatible: true, price: null, included: false };
                            return (
                              <div key={mp.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                                <span className="text-sm text-slate-700 flex-1 font-medium">{mp.name}</span>
                                <div className="flex items-center gap-2">
                                  {/* Compatible checkbox */}
                                  <label className="flex items-center gap-1 text-xs">
                                    <input
                                      type="checkbox"
                                      checked={compat.compatible}
                                      onChange={(e) => updateOptionCompatibility(mp.id, 'compatible', e.target.checked)}
                                      className="w-4 h-4 rounded border-slate-300"
                                    />
                                    <span className={compat.compatible ? 'text-green-600' : 'text-red-500'}>
                                      {compat.compatible ? '✓ Compatible' : '✗ Non compatible'}
                                    </span>
                                  </label>
                                  
                                  {compat.compatible && (
                                    <>
                                      {/* Included checkbox */}
                                      <label className="flex items-center gap-1 text-xs ml-2">
                                        <input
                                          type="checkbox"
                                          checked={compat.included}
                                          onChange={(e) => updateOptionCompatibility(mp.id, 'included', e.target.checked)}
                                          className="w-4 h-4 rounded border-slate-300"
                                        />
                                        <span className={compat.included ? 'text-blue-600 font-medium' : 'text-slate-500'}>
                                          Inclus
                                        </span>
                                      </label>
                                      
                                      {/* Price input (only if not included) */}
                                      {!compat.included && (
                                        <Input
                                          type="number"
                                          step="0.01"
                                          placeholder="Prix €"
                                          value={compat.price || ''}
                                          onChange={(e) => updateOptionCompatibility(mp.id, 'price', parseFloat(e.target.value) || null)}
                                          className="w-24 h-8 text-sm"
                                          data-testid={`opt-price-${mp.id}`}
                                        />
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-slate-500">
                          ✓ Compatible + Prix = Option payante | ✓ Compatible + Inclus = Option incluse dans le prix de base | ✗ Non compatible = Option non disponible pour cette machine
                        </p>
                      </div>
                    )}
                    
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setOptDialogOpen(false)}>
                        Annuler
                      </Button>
                      <Button
                        type="submit"
                        className="bg-slate-800 hover:bg-slate-900"
                        disabled={formLoading}
                        data-testid="save-opt-btn"
                      >
                        {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {editingOpt ? 'Mettre à jour' : 'Créer'}
                      </Button>
                    </div>
                  </form>
                  
                  {/* Media Selector Dialog for Options */}
                  <MediaSelector
                    open={optMediaDialogOpen}
                    onOpenChange={setOptMediaDialogOpen}
                    fileType="image"
                    title="Sélectionner une image pour l'option"
                    onSelect={(selected) => {
                      setOptForm({ ...optForm, image_url: selected.url });
                      setOptImagePreview(null);
                    }}
                  />
                </DialogContent>
              </Dialog>
          </div>

          {/* Options grouped by category */}
          <div className="space-y-8">
            {categories.map((cat) => {
              const catOptions = options.filter((o) => o.category === cat.id);
              if (catOptions.length === 0) return null;
              
              return (
                <div key={cat.id}>
                  <h3 className="text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                    {cat.name} ({catOptions.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {catOptions.map((opt) => {
                      // Get compatible models info
                      const compatInfo = opt.compatibility_by_model || {};
                      const compatibleModels = miniPelles.filter(mp => {
                        const info = compatInfo[mp.id];
                        return info?.compatible !== false;
                      });
                      const includedModels = miniPelles.filter(mp => {
                        const info = compatInfo[mp.id];
                        return info?.included === true;
                      });
                      
                      return (
                      <Card key={opt.id} className="border-slate-200" data-testid={`opt-card-${opt.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            {getImageUrl(opt.image_url) ? (
                              <img
                                src={getImageUrl(opt.image_url)}
                                alt={opt.name}
                                className="w-16 h-16 object-cover rounded-md"
                              />
                            ) : (
                              <div className="w-16 h-16 bg-slate-100 rounded-md flex items-center justify-center">
                                <Wrench className="w-6 h-6 text-slate-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-slate-800">{opt.name}</h4>
                              {opt.description && (
                                <p className="text-sm text-slate-500 line-clamp-2">{opt.description}</p>
                              )}
                              {Object.keys(opt.prices_by_model || {}).length > 0 && (
                                <p className="text-sm text-yellow-600 font-mono mt-1">
                                  À partir de {formatPrice(Math.min(...Object.values(opt.prices_by_model)))}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Compatible models display */}
                          {(compatibleModels.length > 0 || Object.keys(compatInfo).length > 0) && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-xs text-slate-500 mb-2">Modèles compatibles :</p>
                              <div className="flex flex-wrap gap-1">
                                {compatibleModels.slice(0, 5).map(mp => {
                                  const info = compatInfo[mp.id] || {};
                                  const isIncluded = info.included === true;
                                  return (
                                    <Badge 
                                      key={mp.id} 
                                      className={isIncluded 
                                        ? "bg-green-100 text-green-700 text-xs" 
                                        : "bg-slate-100 text-slate-600 text-xs"
                                      }
                                    >
                                      {isIncluded && <CheckCircle className="w-3 h-3 mr-1" />}
                                      {mp.name.split(' ')[0]}
                                    </Badge>
                                  );
                                })}
                                {compatibleModels.length > 5 && (
                                  <Badge className="bg-slate-100 text-slate-600 text-xs">
                                    +{compatibleModels.length - 5}
                                  </Badge>
                                )}
                                {compatibleModels.length === 0 && (
                                  <span className="text-xs text-slate-400">Tous les modèles</span>
                                )}
                              </div>
                              {includedModels.length > 0 && (
                                <p className="text-xs text-green-600 mt-1">
                                  ✓ Inclus sur {includedModels.length} modèle(s)
                                </p>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditOpt(opt)}
                              data-testid={`edit-opt-${opt.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteOpt(opt.id)}
                              className="text-red-500 hover:text-red-600"
                              data-testid={`delete-opt-${opt.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                    })}
                  </div>
                </div>
              );
            })}
            {options.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                Aucune option dans le catalogue
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Import Dialog */}
      <CatalogueImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImportSuccess={fetchData}
      />
    </div>
  );
};

export default CataloguePage;
